import React, { useState } from 'react';
import axios from 'axios';
import { 
  ChevronLeft, BookOpen, Play, Target, Plus, Loader2, Trash2, X, Pencil, Check, ArrowRightLeft, Copy
} from 'lucide-react';
import PodcastPlayer from '../components/PodcastPlayer';
import CommunityShare from '../components/CommunityShare';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shared course IDs that are common between nursing and medicine
const SHARED_COURSE_IDS = [
  'anatomy', 'physiology', 'biochemistry', 'pharmacology', 'internal-medicine', 
  'surgery', 'pediatrics', 'ob-gyn', 'mental-health',
  'md-anatomy', 'md-physiology', 'md-biochemistry', 'md-pharmacology', 
  'md-internal', 'md-surgery', 'md-pediatrics', 'md-obgyn', 'md-psychiatry'
];

function CopyButton({ text, label, dark = true }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95 ${
        copied 
          ? 'bg-green-500 text-white' 
          : dark 
            ? 'bg-white/10 hover:bg-white/20 text-slate-300' 
            : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
      }`}
      data-testid={`copy-btn-${label}`}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'הועתק!' : label}
    </button>
  );
}

function MoveVideoPanel({ allCourses, currentCourseId, onMove, onCancel, index }) {
  const [search, setSearch] = useState('');
  const filtered = (allCourses || [])
    .filter(c => c.id !== currentCourseId)
    .filter(c => c.name.includes(search));

  return (
    <div className="absolute top-2 left-12 z-20 bg-white rounded-xl shadow-lg p-3 border border-blue-300 w-[280px] max-h-[320px] flex flex-col" onClick={(e) => e.stopPropagation()}>
      <p className="text-xs font-bold text-slate-700 mb-2 text-right">העברה לקורס:</p>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="חפשי קורס..."
        className="w-full px-3 py-2 border rounded-lg text-sm text-right focus:ring-2 focus:ring-blue-300 outline-none mb-2"
        dir="rtl"
        autoFocus
        data-testid={`move-search-${index}`}
      />
      <div className="overflow-y-auto flex-1 space-y-1">
        {filtered.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-2">לא נמצאו קורסים</p>
        )}
        {filtered.map(c => (
          <button
            key={c.id}
            onClick={() => onMove(c.id)}
            className="w-full text-right px-3 py-2 text-sm rounded-lg hover:bg-blue-50 hover:text-blue-700 transition-colors"
            data-testid={`move-to-${c.id}`}
          >
            {c.name}
          </button>
        ))}
      </div>
      <button onClick={onCancel} className="mt-2 text-xs text-slate-400 hover:text-slate-600 text-center w-full">
        ביטול
      </button>
    </div>
  );
}

export default function CourseDetailView({
  course,
  specId,
  questionCounts,
  courseVideos,
  onBack,
  onStartQuiz,
  onStartChapterQuiz,
  onAddVideos,
  onDeleteVideo,
  onFileUpload,
  onMoveVideo,
  allCourses,
  activeVideo,
  setActiveVideo,
  showVideoUpload,
  setShowVideoUpload,
  newVideoData,
  setNewVideoData,
  uploadingVideo,
  uploadProgress,
  userName,
  userEmail
}) {
  const isSharedCourse = course?.id ? SHARED_COURSE_IDS.includes(course.id) : false;
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [uploadMode, setUploadMode] = useState('url');
  const [isDragging, setIsDragging] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [movingVideoId, setMovingVideoId] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('video/'));
    if (files.length > 0) onFileUpload(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) onFileUpload(files);
    e.target.value = '';
  };

  const handleRenameVideo = async (videoId) => {
    if (!editTitle.trim()) return;
    try {
      await axios.patch(`${API}/admin/videos/${videoId}`, { title: editTitle.trim() });
      // Update locally
      const idx = courseVideos.findIndex(v => (v.video_id || v.id) === videoId);
      if (idx !== -1) courseVideos[idx].title = editTitle.trim();
      setEditingVideoId(null);
    } catch (e) {
      console.error('Failed to rename video:', e);
    }
  };

  if (!course) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">הקורס לא נמצא</p>
        <button onClick={onBack} className="mt-4 text-teal-600 hover:underline">
          חזרה לקורסים
        </button>
      </div>
    );
  }

  // Defense-in-depth: block direct access to "coming soon" courses
  if (course.coming_soon === true) {
    return (
      <div className="space-y-6 animate-zoom-in" data-testid="course-coming-soon">
        <button
          onClick={onBack}
          className="text-slate-400 hover:text-slate-900 transition-colors font-bold flex items-center gap-2"
          data-testid="back-to-courses-button"
        >
          <ChevronLeft className="w-5 h-5" />
          חזרה לקורסים
        </button>
        <div className="bg-white p-10 rounded-3xl shadow-xl border text-center">
          <div className="text-6xl mb-4">🔒</div>
          <span className="inline-block bg-amber-100 text-amber-700 text-xs font-black px-3 py-1 rounded-full border border-amber-300 mb-4">
            בקרוב
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mb-3">
            {course.name}
          </h2>
          <p className="text-slate-600 max-w-lg mx-auto leading-relaxed">
            אנו עובדים על איסוף חומרי לימוד רשמיים לקורס זה מהמכללה.<br/>
            השאלות והתרגולים יהיו זמינים בקרוב.
          </p>
          {course.source && (
            <p className="text-xs text-slate-400 mt-4 max-w-lg mx-auto">
              📚 מקור סילבוס: {course.source}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-zoom-in">
      <button 
        onClick={onBack}
        className="text-slate-400 hover:text-slate-900 transition-colors font-bold flex items-center gap-2"
        data-testid="back-to-courses-button"
      >
        <ChevronLeft className="w-5 h-5" />
        חזרה לקורסים
      </button>

      {/* Course Header */}
      <div className="bg-white p-8 sm:p-12 rounded-3xl shadow-xl border relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-teal-500 to-emerald-500"></div>
        
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-teal-100 text-teal-800 text-xs font-bold px-3 py-1 rounded-full">
                שנה {course.year}
              </span>
              {course.credits && (
                <span className="bg-slate-100 text-slate-600 text-xs font-bold px-3 py-1 rounded-full">
                  {course.credits} נ"ז
                </span>
              )}
              <span className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full">
                {questionCounts[course.id]?.total || course.question_count || 0} שאלות
              </span>
              {isSharedCourse && (
                <span className="bg-purple-100 text-purple-800 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                  <span>🔗</span> משותף סיעוד+רפואה
                </span>
              )}
            </div>
            
            <h2 className="text-3xl sm:text-5xl font-black text-slate-900 mb-4" data-testid="course-title">
              {course.name}
            </h2>
            <p className="text-lg text-slate-500 font-medium">
              {course.description}
            </p>
          </div>
          
          {/* Quick Exam Button - Uses course questions */}
          <div className="lg:w-64 flex flex-col justify-center">
            <button 
              onClick={() => onStartChapterQuiz({ id: 'all', name: course.name }, course.id, 'mixed')}
              className="w-full bg-teal-500 hover:bg-teal-400 text-white py-4 rounded-2xl font-black text-lg transition-all active:scale-95 shadow-lg"
              data-testid="quick-course-exam-button"
            >
              מבחן כללי בקורס ⚡
            </button>
          </div>
        </div>
      </div>

      {/* Shared Course Notice */}
      {isSharedCourse && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-2xl border border-purple-100 flex items-center gap-4">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center text-xl">
            🔗
          </div>
          <div className="flex-1">
            <p className="text-purple-900 font-bold text-sm">קורס משותף לסיעוד ורפואה</p>
            <p className="text-purple-600 text-xs">בנק השאלות והסרטונים משותפים לשני המסלולים</p>
          </div>
        </div>
      )}

      {/* Course Videos Section */}
      <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg border">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
              <Play className="w-5 h-5 text-rose-600" />
            </div>
            <h3 className="text-xl font-black text-slate-900">סקירה בוידאו</h3>
            <span className="bg-rose-100 text-rose-700 text-xs font-bold px-2 py-1 rounded-full">{courseVideos.length} סרטונים</span>
          </div>
          {/* Admin Add Video Button */}
          <button
            onClick={() => setShowVideoUpload(!showVideoUpload)}
            className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all"
            data-testid="add-video-btn"
          >
            <Plus className="w-4 h-4" />
            הוסף סרטון
          </button>
        </div>

        {/* Video Upload Form */}
        {showVideoUpload && (
          <div className="mb-6 bg-slate-50 p-5 rounded-2xl border-2 border-dashed border-teal-300">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-slate-900">הוספת סרטונים ל-{course.name}</h4>
              <button onClick={() => setShowVideoUpload(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tab switcher */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setUploadMode('url')}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${uploadMode === 'url' ? 'bg-teal-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-100 border'}`}
                data-testid="upload-mode-url"
              >
                קישור URL
              </button>
              <button
                onClick={() => setUploadMode('file')}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${uploadMode === 'file' ? 'bg-teal-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-100 border'}`}
                data-testid="upload-mode-file"
              >
                העלאת קובץ
              </button>
            </div>

            {uploadMode === 'url' ? (
              <>
                <p className="text-xs text-slate-500 mb-3">הדביקו קישורי Google Drive / YouTube — קישור אחד בכל שורה.</p>
                <textarea
                  placeholder={"https://drive.google.com/file/d/.../view\nhttps://drive.google.com/file/d/.../view\nhttps://youtu.be/..."}
                  value={newVideoData.urls}
                  onChange={(e) => setNewVideoData({...newVideoData, urls: e.target.value})}
                  className="w-full px-4 py-3 border rounded-xl text-left font-mono text-sm h-32 resize-y focus:ring-2 focus:ring-teal-300 focus:border-teal-400 outline-none"
                  dir="ltr"
                  data-testid="video-urls-input"
                />
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-slate-400">
                    {newVideoData.urls ? newVideoData.urls.split('\n').filter(u => u.trim()).length : 0} קישורים
                  </span>
                  <button
                    onClick={onAddVideos}
                    disabled={!newVideoData.urls?.trim() || uploadingVideo}
                    className="px-5 py-2.5 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-300 text-white rounded-xl text-sm font-bold flex items-center gap-2 transition-all"
                    data-testid="save-videos-btn"
                  >
                    {uploadingVideo ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> שומר...</>
                    ) : (
                      <><Plus className="w-4 h-4" /> הוסף סרטונים</>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${isDragging ? 'border-teal-500 bg-teal-50' : 'border-slate-300 hover:border-teal-400 hover:bg-slate-100'}`}
                onClick={() => !uploadingVideo && fileInputRef.current?.click()}
                data-testid="file-drop-zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                {uploadingVideo ? (
                  <div className="space-y-3">
                    <Loader2 className="w-10 h-10 text-teal-500 animate-spin mx-auto" />
                    <p className="font-bold text-teal-700">מעלה... {uploadProgress}%</p>
                    <div className="w-full max-w-xs mx-auto bg-slate-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className="bg-teal-500 h-full rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Plus className="w-10 h-10 text-slate-400 mx-auto" />
                    <p className="font-bold text-slate-700">גררו קבצי וידאו לכאן</p>
                    <p className="text-xs text-slate-400">או לחצו לבחירת קבצים מהמחשב</p>
                    <p className="text-xs text-slate-400">MP4, MOV, AVI, WebM</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
          
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {courseVideos.length === 0 && (
            <div className="col-span-full text-center py-8 text-slate-400">
              <Play className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="font-bold">אין סרטונים עדיין</p>
              <p className="text-sm">לחצו "הוסף סרטון" כדי להוסיף קישורים</p>
            </div>
          )}
          {courseVideos.map((video, index) => {
            // Use video_id or title as unique identifier since API returns video_id not id
            const videoKey = video.video_id || video.id || `video-${index}`;
            const isPlaying = activeVideo && (
              (activeVideo.video_id && activeVideo.video_id === video.video_id) ||
              (activeVideo.id && activeVideo.id === video.id) ||
              (activeVideo.title && activeVideo.title === video.title)
            );
            
            return (
            <div 
              key={videoKey}
              data-testid={`video-card-${index}`}
              className={`bg-slate-50 rounded-2xl p-4 transition-all group relative ${(video.vimeo_url || video.video_url) ? 'hover:bg-slate-100' : 'opacity-60'}`}
            >
              {/* Delete Button - Two-click confirmation */}
              {confirmDeleteId === (video.video_id || video.id) ? (
                <div className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-white rounded-full shadow-lg px-2 py-1 border border-red-300">
                  <button
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      onDeleteVideo(video.video_id || video.id);
                      setConfirmDeleteId(null);
                    }}
                    className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full transition-all"
                    data-testid={`confirm-delete-${index}`}
                  >
                    מחק
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                    className="text-slate-400 hover:text-slate-600 p-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setConfirmDeleteId(video.video_id || video.id);
                  }}
                  className="absolute top-2 left-2 z-10 bg-red-500 hover:bg-red-600 text-white w-8 h-8 rounded-full flex items-center justify-center shadow-md transition-all active:scale-90"
                  data-testid={`delete-video-${index}`}
                  title="מחק סרטון"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}

              {/* Move Button */}
              {movingVideoId === (video.video_id || video.id) ? (
                <MoveVideoPanel
                  allCourses={allCourses}
                  currentCourseId={course.id}
                  onMove={(targetSlug) => {
                    onMoveVideo(video.video_id || video.id, targetSlug);
                    setMovingVideoId(null);
                  }}
                  onCancel={() => setMovingVideoId(null)}
                  index={index}
                />
              ) : (
                <button
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setMovingVideoId(video.video_id || video.id);
                    setConfirmDeleteId(null);
                  }}
                  className="absolute top-12 left-2 z-10 bg-blue-500 hover:bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center shadow-md transition-all active:scale-90"
                  data-testid={`move-video-${index}`}
                  title="העבר לקורס אחר"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                </button>
              )}

              {/* Video Player or Thumbnail */}
              {isPlaying ? (
                // Playing video inline
                <div className="aspect-video bg-black rounded-xl mb-3 overflow-hidden relative" data-video-container={`video-${index}`}>
                  {(video.vimeo_url || video.video_url)?.includes('drive.google.com') ? (
                    <iframe
                      src={(video.vimeo_url || video.video_url)?.includes('/preview') ? (video.vimeo_url || video.video_url) : (video.vimeo_url || video.video_url)?.replace('/view', '/preview').split('?')[0]}
                      className="w-full h-full"
                      allow="autoplay; encrypted-media; fullscreen"
                      allowFullScreen
                    />
                  ) : (video.vimeo_url || video.video_url) ? (
                    <video 
                      controls 
                      autoPlay
                      playsInline
                      className="w-full h-full"
                    >
                      <source src={video.vimeo_url || video.video_url} type="video/mp4" />
                    </video>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-500">
                      אין סרטון זמין
                    </div>
                  )}
                  <button 
                    onClick={() => setActiveVideo(null)}
                    className="absolute top-2 right-2 bg-black/70 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/90 z-10"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                // Thumbnail - click to play
                <div 
                  onClick={() => (video.video_url || video.vimeo_url) && setActiveVideo(video)}
                  className="aspect-video bg-slate-200 rounded-xl mb-3 flex items-center justify-center relative overflow-hidden cursor-pointer"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-emerald-500/20"></div>
                  <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 text-teal-600 mr-[-2px]" />
                  </div>
                  {video.video_url || video.vimeo_url || video.youtube_url ? (
                    <span className="absolute bottom-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">זמין</span>
                  ) : (
                    <span className="absolute bottom-2 right-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">בקרוב</span>
                  )}
                </div>
              )}
              {isPlaying && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    // Save references SYNCHRONOUSLY before any await
                    const card = e.currentTarget.closest('[data-testid^="video-card-"]') || e.currentTarget.parentElement;
                    const vid = card?.querySelector('video');
                    const ifr = card?.querySelector('iframe');
                    const url = video.vimeo_url || video.video_url;
                    const isDrive = url?.includes('drive.google.com');

                    if (isDrive) {
                      if (ifr) ifr.src = 'about:blank';
                      setActiveVideo(null);
                      const driveUrl = url.replace('/preview', '/view');
                      setTimeout(() => {
                        window.open(driveUrl, '_blank', 'noopener,noreferrer');
                      }, 50);
                      return;
                    }

                    if (vid) {
                      try {
                        if (vid.requestFullscreen) await vid.requestFullscreen();
                        else if (vid.webkitRequestFullscreen) vid.webkitRequestFullscreen();
                        else if (vid.webkitEnterFullscreen) vid.webkitEnterFullscreen();
                        else if (vid.mozRequestFullScreen) vid.mozRequestFullScreen();
                        else if (vid.msRequestFullscreen) vid.msRequestFullscreen();
                        else {
                          vid.pause();
                          setActiveVideo(null);
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }
                      } catch (err) {
                        // Final fallback - open mp4 in new tab
                        try { vid.pause(); } catch(_) {}
                        setActiveVideo(null);
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }
                      return;
                    }

                    // Couldn't find video element at all - open URL directly
                    if (url) {
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  className="w-full mb-3 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold py-2 px-4 rounded-lg shadow-sm transition-colors"
                  data-testid={`fullscreen-video-${index}`}
                  title="פתח במסך מלא"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></svg>
                  הגדל למסך מלא
                </button>
              )}
              <h4 className="font-bold text-slate-900 text-sm mb-1">
                {editingVideoId === (video.video_id || video.id) ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRenameVideo(video.video_id || video.id); if (e.key === 'Escape') setEditingVideoId(null); }}
                      className="flex-1 px-2 py-1 border border-teal-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-300 outline-none"
                      autoFocus
                      dir="rtl"
                      data-testid={`rename-input-${index}`}
                    />
                    <button
                      onClick={() => handleRenameVideo(video.video_id || video.id)}
                      className="text-teal-600 hover:text-teal-800 p-1"
                      data-testid={`rename-save-${index}`}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingVideoId(null)}
                      className="text-slate-400 hover:text-slate-600 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <span
                    className="cursor-pointer hover:text-teal-700 transition-colors inline-flex items-center gap-1 line-clamp-2"
                    onClick={(e) => { e.stopPropagation(); setEditingVideoId(video.video_id || video.id); setEditTitle(video.title); }}
                    title="לחצי לשינוי שם"
                    data-testid={`rename-video-${index}`}
                  >
                    {video.title}
                    <Pencil className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  </span>
                )}
              </h4>
            </div>
            );
          })}
        </div>
      </div>

      {/* Community Sharing - after videos */}
      <CommunityShare
        courseId={course.id}
        courseName={course.name}
        specialization={specId}
        userName={userName}
        userEmail={userEmail}
      />

      {/* Course Summary */}
      {course.summary && (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 sm:p-10 rounded-3xl shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-teal-500 rounded-2xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-black text-teal-400">תקציר הקורס</h3>
            </div>
            <CopyButton text={course.summary} label="העתק תקציר" />
          </div>
          <div className="prose prose-invert max-w-none">
            <p className="text-slate-300 font-medium leading-relaxed whitespace-pre-line text-base">
              {course.summary}
            </p>
          </div>
        </div>
      )}

      {/* Podcast Player */}
      <PodcastPlayer 
        specId={specId} 
        courseId={course.id}
        courseName={course.name}
        summary={course.summary}
      />

      {/* Chapters Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chapters/Syllabus */}
        <div className="bg-white p-8 rounded-3xl shadow-lg border">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-black text-slate-900">סילבוס הקורס</h3>
                <p className="text-slate-400 text-sm">{course.chapters?.length || 0} פרקים • בחר פרק ורמת קושי</p>
              </div>
            </div>
            <CopyButton 
              text={course.chapters?.map((ch, i) => `${i+1}. ${ch.name}${ch.description ? ' - ' + ch.description : ''}`).join('\n') || ''} 
              label="העתק פרקים"
              dark={false}
            />
          </div>
          
          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
            {course.chapters?.map((chapter, index) => {
              const isSelected = selectedChapter?.id === chapter.id;
              return (
                <div key={chapter.id}>
                  <button
                    onClick={() => setSelectedChapter(isSelected ? null : chapter)}
                    className={`w-full p-4 rounded-xl transition-all text-right group cursor-pointer ${
                      isSelected 
                        ? 'bg-blue-50 border-2 border-blue-400 shadow-md' 
                        : 'bg-slate-50 hover:bg-blue-50 border-2 border-transparent hover:border-blue-200'
                    }`}
                    data-testid={`chapter-button-${chapter.id}`}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0 transition-all ${
                        isSelected ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-600 group-hover:bg-blue-500 group-hover:text-white'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <span className={`font-bold ${isSelected ? 'text-blue-700' : 'text-slate-900 group-hover:text-blue-700'}`}>
                            {chapter.name}
                          </span>
                          <span className={`text-xs px-3 py-1 rounded-full font-bold transition-all ${
                            isSelected ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-600'
                          }`}>
                            {isSelected ? 'נבחר ✓' : 'התחל מבחן בפרק'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400 mt-1">{chapter.description}</p>
                      </div>
                    </div>
                  </button>

                  {/* Difficulty Selection - appears when chapter is selected */}
                  {isSelected && (
                    <div className="mt-2 mr-11 bg-blue-50 rounded-xl p-4 border border-blue-200 animate-slide-up" data-testid="difficulty-selector">
                      <p className="text-sm font-bold text-blue-800 mb-3">בחר רמת קושי:</p>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <button
                          onClick={() => setSelectedDifficulty('easy')}
                          className={`py-2.5 rounded-lg font-bold text-sm transition-all active:scale-95 ${
                            selectedDifficulty === 'easy' 
                              ? 'bg-emerald-500 text-white shadow-md' 
                              : 'bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                          }`}
                          data-testid="chapter-difficulty-easy"
                        >
                          קלה 🌱
                        </button>
                        <button
                          onClick={() => setSelectedDifficulty('medium')}
                          className={`py-2.5 rounded-lg font-bold text-sm transition-all active:scale-95 ${
                            selectedDifficulty === 'medium' 
                              ? 'bg-amber-500 text-white shadow-md' 
                              : 'bg-white border border-amber-300 text-amber-700 hover:bg-amber-50'
                          }`}
                          data-testid="chapter-difficulty-medium"
                        >
                          בינונית ⚡
                        </button>
                        <button
                          onClick={() => setSelectedDifficulty('hard')}
                          className={`py-2.5 rounded-lg font-bold text-sm transition-all active:scale-95 ${
                            selectedDifficulty === 'hard' 
                              ? 'bg-rose-500 text-white shadow-md' 
                              : 'bg-white border border-rose-300 text-rose-700 hover:bg-rose-50'
                          }`}
                          data-testid="chapter-difficulty-hard"
                        >
                          קשה 🔥
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          onStartChapterQuiz(chapter, course.id, selectedDifficulty || 'mixed');
                          setSelectedChapter(null);
                          setSelectedDifficulty(null);
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-black text-base transition-all active:scale-95 shadow-md"
                        data-testid="start-chapter-exam-button"
                      >
                        התחל מבחן בפרק {chapter.name} →
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Exam Options */}
        <div className="bg-slate-900 text-white p-8 rounded-3xl flex flex-col justify-between shadow-xl">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-500 rounded-2xl flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-black text-teal-400">מבחנים מובנים</h3>
            </div>
            <p className="text-slate-300 font-medium mb-8">
              בחר רמת קושי לתרגול ממוקד בקורס {course.name}. כל מבחן כולל 25 שאלות עם הסברים מפורטים.
            </p>
          </div>
          <div className="space-y-3">
            <button 
              onClick={() => onStartChapterQuiz({ id: 'all', name: course.name }, course.id, 'easy')}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 py-4 rounded-xl font-black text-lg transition-all active:scale-95"
              data-testid="course-easy-exam-button"
            >
              רמה קלה (יסודות) 🌱
            </button>
            <button 
              onClick={() => onStartChapterQuiz({ id: 'all', name: course.name }, course.id, 'medium')}
              className="w-full bg-teal-500 hover:bg-teal-400 text-slate-900 py-4 rounded-xl font-black text-lg transition-all active:scale-95"
              data-testid="course-medium-exam-button"
            >
              רמה בינונית (יישום) ⚡
            </button>
            <button 
              onClick={() => onStartChapterQuiz({ id: 'all', name: course.name }, course.id, 'hard')}
              className="w-full bg-rose-500 hover:bg-rose-400 text-white py-4 rounded-xl font-black text-lg transition-all active:scale-95"
              data-testid="course-hard-exam-button"
            >
              רמה קשה (מתקדם) 🔥
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
